// src/components/UserRegistrationForm.js
import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, collection, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';
import { db } from '../firebase'; // Assurez-vous que votre instance db est importée correctement
import { getAuth } from 'firebase/auth'; // Importez getAuth si auth n'est pas passé en prop

const UserRegistrationForm = ({ setMessage, setError, auth, userRole: currentUserRole, schoolId: currentUsersSchoolId, allSchoolsList, schoolName: currentUsersSchoolName }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState(''); // Le rôle sélectionné par l'admin qui crée l'utilisateur
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSchoolForRegistration, setSelectedSchoolForRegistration] = useState(currentUsersSchoolId || '');
  const [selectedSchoolNameForRegistration, setSelectedSchoolNameForRegistration] = useState(currentUsersSchoolName || '');

  // Mappage des rôles techniques (en anglais) vers les libellés en français
  const roleLabels = {
    superAdmin: 'Super Administrateur',
    admin: 'Administrateur d\'école',
    enseignant: 'Enseignant', // Changé de 'teacher' à 'enseignant' pour correspondre à vos règles
    etudiant: 'Élève',       // Changé de 'student' à 'etudiant' pour correspondre à vos règles
    secretaireGeneral: 'Secrétaire général',
    directeurPedagogique: 'Directeur pédagogique', // Changé de 'director'
    financier: 'Financier'
  };

  // Options de rôle disponibles basées sur le rôle de l'utilisateur actuel (qui crée)
  const roleOptions = currentUserRole === 'superAdmin'
    ? ['superAdmin', 'admin', 'enseignant', 'etudiant', 'secretaireGeneral', 'directeurPedagogique', 'financier']
    : ['admin', 'enseignant', 'etudiant', 'secretaireGeneral', 'directeurPedagogique', 'financier'];

  useEffect(() => {
    // Si l'utilisateur actuel n'est pas superAdmin, le schoolId est fixé et non modifiable
    if (currentUserRole !== 'superAdmin' && currentUsersSchoolId) {
      setSelectedSchoolForRegistration(currentUsersSchoolId);
      setSelectedSchoolNameForRegistration(currentUsersSchoolName);
    }
  }, [currentUserRole, currentUsersSchoolId, currentUsersSchoolName]);

  // Gérer le changement de sélection de l'école pour les Super Admins
  const handleSchoolSelectionChange = (e) => {
    const newSchoolId = e.target.value;
    setSelectedSchoolForRegistration(newSchoolId);
    // Trouver le nom de l'école correspondant à l'ID sélectionné
    const selectedSchool = allSchoolsList.find(school => school.id === newSchoolId);
    setSelectedSchoolNameForRegistration(selectedSchool ? selectedSchool.name : null); // Utilisez null si pas de nom
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsLoading(true);

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      setIsLoading(false);
      return;
    }
    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      setIsLoading(false);
      return;
    }
    if (!role) {
      setError("Veuillez sélectionner un rôle.");
      setIsLoading(false);
      return;
    }
    // Pour les rôles d'école, l'ID de l'école est obligatoire
   if (['admin', 'enseignant', 'etudiant', 'secretaireGeneral', 'directeurPedagogique', 'financier'].includes(role) && !selectedSchoolForRegistration) {
      setError("Veuillez sélectionner l'école pour cet utilisateur.");
      setIsLoading(false);
      return;
   }

    try {
      // Création de l'utilisateur Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Création du profil utilisateur dans Firestore
      const userProfileData = {
        uid: user.uid, // Utiliser 'uid' au lieu de 'userId' pour correspondre aux règles 'users/{userId}'
        email: email,
        role: role, // ✅ Utilise le rôle sélectionné par l'administrateur
        createdAt: serverTimestamp(),
        profileCompleted: true, // ✅ Indique que le profil est complet (car créé par un admin)
      };

      // Si le rôle n'est pas 'superAdmin', associer l'utilisateur à une école
      if (role !== 'superAdmin') {
        userProfileData.schoolId = selectedSchoolForRegistration || null;
        userProfileData.schoolName = selectedSchoolNameForRegistration || null;
      }
      // Si le rôle est 'superAdmin', schoolId et schoolName ne sont pas inclus, ce qui est correct.

      await setDoc(doc(db, 'users', user.uid), userProfileData);

      setMessage({ type: 'success', text: "Utilisateur enregistré avec succès et profil créé !" });
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setRole('');
      if (currentUserRole === 'superAdmin') {
        setSelectedSchoolForRegistration('');
        setSelectedSchoolNameForRegistration('');
      }
    } catch (err) {
      console.error("Erreur lors de l'enregistrement:", err);
      let errorMessage = "Échec de l'enregistrement. Veuillez réessayer.";
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = "Cette adresse email est déjà utilisée.";
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = "L'adresse email est invalide.";
      } else if (err.code === 'auth/weak-password') {
        errorMessage = "Le mot de passe est trop faible.";
      } else if (err.code === 'permission-denied') {
        errorMessage = "Permission refusée. Vous n'êtes pas autorisé à créer cet utilisateur ou son rôle.";
      }
      setError(`Erreur: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Création de Nouvel Utilisateur</h2>
      <form onSubmit={handleRegister} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">Mot de passe</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">Confirmer le mot de passe</label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700">Rôle</label>
          <select
            id="role"
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              // Si le rôle n'est pas superAdmin et qu'un schoolId est disponible, le présélectionner
              if (e.target.value !== 'superAdmin' && currentUsersSchoolId) {
                setSelectedSchoolForRegistration(currentUsersSchoolId);
                setSelectedSchoolNameForRegistration(currentUsersSchoolName);
              } else if (e.target.value === 'superAdmin') {
                setSelectedSchoolForRegistration(''); // Pas de schoolId pour superAdmin
                setSelectedSchoolNameForRegistration('');
              }
            }}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="">Sélectionner un rôle</option>
            {roleOptions.map(optionRole => (
              <option key={optionRole} value={optionRole}>{roleLabels[optionRole] || optionRole}</option>
            ))}
          </select>
        </div>

        {/* Sélecteur d'école CONDITIONNEL */}
        {(currentUserRole === 'superAdmin' || (role && role !== 'superAdmin')) && (
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <label htmlFor="schoolForUser" className="block text-sm font-medium text-gray-700 mb-1">
              {currentUserRole === 'superAdmin' ? "Sélectionner l'école pour cet utilisateur" : "École associée"}
            </label>
            <select
              id="schoolForUser"
              value={selectedSchoolForRegistration}
              onChange={handleSchoolSelectionChange}
              required={role && role !== 'superAdmin'}
              disabled={currentUserRole !== 'superAdmin' && !!currentUsersSchoolId}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">{currentUserRole === 'superAdmin' ? "-- Choisir une école --" : (currentUsersSchoolName || "Chargement...")}</option>
              {currentUserRole === 'superAdmin' && allSchoolsList.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.id})</option>
              ))}
              {currentUserRole !== 'superAdmin' && currentUsersSchoolId && (
                <option value={currentUsersSchoolId} disabled>{currentUsersSchoolName || 'Votre école'}</option>
              )}
            </select>
            {currentUserRole !== 'superAdmin' && currentUsersSchoolId && (
              <p className="text-xs text-gray-500 mt-1">
                En tant qu'administrateur d'école, les utilisateurs que vous créez seront automatiquement liés à votre école.
              </p>
            )}
          </div>
        )}

        <button
          type="submit"
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          disabled={isLoading}
        >
          {isLoading ? <Loader2 className="animate-spin mr-2" size={20} /> : "Enregistrer l'utilisateur"}
        </button>
      </form>
    </div>
  );
};

export default UserRegistrationForm;
